import React, { useState, useEffect } from "react";
import { DatePicker } from "antd";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import "./editProject.css";
import { BASE_URL } from "../apiConfig";
import { message } from "antd";
import "dayjs/locale/en";
import { ChevronLeft, Trash2 } from "lucide-react";
import Loading from "./loading";

dayjs.locale("en");

const EditProject = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const { projectId, roleId, clientID } = useParams();
  const location = useLocation();

  const [clientName, setClientName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [departments, setDepartments] = useState([""]);
  const [designations, setDesignations] = useState([""]);
  const [isLoading, setIsLoading] = useState(true);
  const [inputId, setInputId] = useState("");
  //   const [currentProject, setCurrentProject] = useState(null);
  const [showCopyProjectModal, setShowCopyProjectModal] = useState(false);
  const [dataUploaded, setDataUploaded] = useState(false);

  // Restrict all date pickers to ±10 years from today
  const datePickerMinDate = dayjs().subtract(10, "year").startOf("day");
  const datePickerMaxDate = dayjs().add(10, "year").endOf("day");
  const disabledDateOutsideRange = (current) => {
    if (!current) return false;
    return (
      current.isBefore(datePickerMinDate, "day") ||
      current.isAfter(datePickerMaxDate, "day")
    );
  };

  // Form validation errors
  const [errors, setErrors] = useState({
    clientName: "",
    projectName: "",
    spocName: "",
    projectStartDate: "",
    studyBeginDate: "",
    studyEndDate: "",
    dateComparison: "",
    departments: "",
    designations: "",
  });

  const [projectDetails, setProjectDetails] = useState({
    spocName: "",
    projectStartDate: null,
    studyBeginDate: null,
    studyEndDate: null,
    earliestDateForDOJ: null,
    endDateForDOJ: null,
  });

  const [projectDetailsSend, setProjectDetailsSend] = useState({
    spocName: "",
    projectStartDate: null,
    studyBeginDate: null,
    studyEndDate: null,
    earliestDateForDOJ: null,
    endDateForDOJ: null,
  });

  // Set project name and client name from location state when available
  useEffect(() => {
    if (location.state && location.state.projectName) {
      setProjectName(location.state.projectName);
    }
    if (location.state && location.state.clientName) {
      setClientName(location.state.clientName);
      console.log("Client Name:", location.state.clientName);
    }
  }, [location.state]);

  useEffect(() => {
    if (projectDetails.studyBeginDate && projectDetails.studyEndDate) {
      const beginDate = dayjs(projectDetails.studyBeginDate);
      const endDate = dayjs(projectDetails.studyEndDate);

      // Calculate month difference
      const monthsDifference = endDate.diff(beginDate, "month");
      const monthsToSubtract = 2 * monthsDifference;

      // Subtract months from end date
      const calculatedDate = endDate.subtract(monthsToSubtract + 1, "month");

      // Keep only the day from begin date, preserve year and month from calculation
      const earliestDate = calculatedDate.date(beginDate.date());

      const endDateForDOJ = endDate;

      setProjectDetails((prev) => ({
        ...prev,
        earliestDateForDOJ: earliestDate,
        endDateForDOJ: endDateForDOJ,
      }));
    }
  }, [projectDetails.studyBeginDate, projectDetails.studyEndDate]);

  // useEffect(() => {
  //   if (projectDetails.studyBeginDate && projectDetails.studyEndDate) {
  //     const beginDate = dayjs(projectDetails.studyBeginDate);
  //     const endDate = dayjs(projectDetails.studyEndDate);
  //     const diffInDays = endDate.diff(beginDate, "month");

  //     const earliestDate = beginDate.subtract(diffInDays + 1, "month");

  //     const endDateForDOJ = endDate;
  //     setProjectDetails((prev) => ({
  //       ...prev,
  //       earliestDateForDOJ: earliestDate,
  //       endDateForDOJ: endDateForDOJ,
  //     }));
  //   }
  // }, [projectDetails.studyBeginDate, projectDetails.studyEndDate]);

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const requestBody = {
          project_id: projectId,
          name: projectName,
          jobrole_id: roleId,
        };

        const response = await fetch(`${BASE_URL}/project/get_project`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });

        if (response.ok) {
          const data = await response.json();
          console.log("FULL API RESPONSE:", JSON.stringify(data, null, 2));

          setDataUploaded(
            data.latest_input_model !== null &&
            data.latest_input_model.input_status !== "data_upload_unsuccessful"
          );

          const resolvedClientName =
            data?.jobRole_details?.client_details?.name ||
            data?.jobRole_details?.client_details?.display_name ||
            location.state?.clientName ||
            "";

          setClientName(resolvedClientName);

          const resolvedProjectName =
            location.state?.projectName ||
            data.name ||
            "";
          setProjectName(resolvedProjectName);

          if (data.project_details) {
            setProjectDetails({
              spocName: data.project_details.client_spoc_name || "",
              projectStartDate: data.project_details.project_start_date
                ? dayjs(data.project_details.project_start_date)
                : null,
              studyBeginDate: data.project_details.study_period_begining_date
                ? dayjs(data.project_details.study_period_begining_date)
                : null,
              studyEndDate: data.project_details.study_period_end_date
                ? dayjs(data.project_details.study_period_end_date)
                : null,
              earliestDateForDOJ: data.project_details
                .earliest_date_for_date_of_joining_relevent_for_the_study
                ? dayjs(
                  data.project_details
                    .earliest_date_for_date_of_joining_relevent_for_the_study
                )
                : null,
              endDateForDOJ: data.project_details
                .end_date_for_date_of_joining_relevent_for_the_study
                ? dayjs(
                  data.project_details
                    .end_date_for_date_of_joining_relevent_for_the_study
                )
                : null,
            });
            setProjectDetailsSend({
              spocName: data.project_details.client_spoc_name || "",
              projectStartDate: data.project_details.project_start_date,
              studyBeginDate: data.project_details.study_period_begining_date,
              studyEndDate: data.project_details.study_period_end_date,
              earliestDateForDOJ:
                data.project_details
                  .earliest_date_for_date_of_joining_relevent_for_the_study,
              endDateForDOJ:
                data.project_details
                  .end_date_for_date_of_joining_relevent_for_the_study,
            });

            // Set departments
            if (
              data.project_details.departments &&
              data.project_details.departments.length > 0
            ) {
              setDepartments(data.project_details.departments);
            }

            // Set designations
            if (
              data.project_details.designations &&
              data.project_details.designations.length > 0
            ) {
              setDesignations(data.project_details.designations);
            }
          }
        } else {
          const errorData = await response.json();
          messageApi.error(errorData.detail || "Failed to fetch project data");
        }
      } catch (error) {
        console.error("Error fetching project data:", error);
        messageApi.error("An error occurred while fetching project data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectData();
  }, [projectId, messageApi, location.state, roleId]);

  const validateClientName = () => {
    if (!clientName.trim()) {
      setErrors((prev) => ({ ...prev, clientName: "Client name is required" }));
      return false;
    }
    setErrors((prev) => ({ ...prev, clientName: "" }));
    return true;
  };

  const validateProjectName = () => {
    const specialCharRegex = /^[a-zA-Z0-9 ]+$/;

    if (!projectName.trim()) {
      setErrors((prev) => ({
        ...prev,
        projectName: "Project name is required",
      }));
      return false;
    } else if (projectName.length < 4) {
      setErrors((prev) => ({
        ...prev,
        projectName: "Minimum Character Limit not met, please contact Admin",
      }));
      return false;
    } else if (projectName.length > 40) {
      setErrors((prev) => ({
        ...prev,
        projectName:
          "Maximum Character Limit Exceeded, please recheck or contact Admin",
      }));
      return false;
    } else if (!specialCharRegex.test(projectName)) {
      setErrors((prev) => ({
        ...prev,
        projectName: "Special characters are not allowed",
      }));
      return false;
    }

    setErrors((prev) => ({ ...prev, projectName: "" }));
    return true;
  };

  const validateDepartments = () => {
    const validDepartments = departments.filter((dept) => dept.trim() !== "");
    if (validDepartments.length === 0) {
      setErrors((prev) => ({
        ...prev,
        departments: "At least one department is required",
      }));
      return false;
    }
    const normalizedDepartments = validDepartments.map((dept) =>
      dept.trim().toLowerCase()
    );
    if (new Set(normalizedDepartments).size !== normalizedDepartments.length) {
      setErrors((prev) => ({
        ...prev,
        departments: "Duplicate department values are not allowed",
      }));
      return false;
    }
    setErrors((prev) => ({ ...prev, departments: "" }));
    return true;
  };

  const validateDesignations = () => {
    const validDesignations = designations.filter(
      (desig) => desig.trim() !== ""
    );
    if (validDesignations.length === 0) {
      setErrors((prev) => ({
        ...prev,
        designations: "At least one designation is required",
      }));
      return false;
    }
    const normalizedDesignations = validDesignations.map((designation) =>
      designation.trim().toLowerCase()
    );
    if (new Set(normalizedDesignations).size !== normalizedDesignations.length) {
      setErrors((prev) => ({
        ...prev,
        designations: "Duplicate designation values are not allowed",
      }));
      return false;
    }
    setErrors((prev) => ({ ...prev, designations: "" }));
    return true;
  };

  const validateProjectDetails = () => {
    let isValid = true;
    const newErrors = { ...errors };

    if (!validateClientName()) {
      isValid = false;
    }

    if (!validateProjectName()) {
      isValid = false;
    }

    if (!projectDetails.spocName.trim()) {
      newErrors.spocName = "SPOC Name is required";
      isValid = false;
    } else {
      newErrors.spocName = "";
    }

    if (!projectDetails.projectStartDate) {
      newErrors.projectStartDate = "Project Start Date is required";
      isValid = false;
    } else {
      newErrors.projectStartDate = "";
    }

    if (!projectDetails.studyBeginDate) {
      newErrors.studyBeginDate = "Study Begin Date is required";
      isValid = false;
    } else {
      newErrors.studyBeginDate = "";
    }

    if (!projectDetails.studyEndDate) {
      newErrors.studyEndDate = "Study End Date is required";
      isValid = false;
    } else {
      newErrors.studyEndDate = "";
    }

    if (projectDetails.studyBeginDate && projectDetails.studyEndDate) {
      if (projectDetails.studyEndDate.isBefore(projectDetails.studyBeginDate)) {
        newErrors.dateComparison =
          "Study Period end date must be a date after the study period beginning date";
        isValid = false;
      } else {
        newErrors.dateComparison = "";
      }
    }

    if (!validateDepartments()) {
      isValid = false;
    }

    if (!validateDesignations()) {
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateProjectDetails()) {
      messageApi.error("Please fill in all required fields correctly");
      return;
    }

    try {
      const token = localStorage.getItem("accessToken");
      const projectNameToUse = projectName || location.state?.projectName;

      const updateProjectBody = {
        name: projectName,
        // client_name: clientName,
        project_id: projectId,
        jobrole_id: roleId,
      };

      const updateProjectResponse = await fetch(`${BASE_URL}/project/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateProjectBody),
      });

      if (!updateProjectResponse.ok) {
        const errorData = await updateProjectResponse.json();
        messageApi.error(errorData.detail || "Failed to update project name");
        return;
      }

      const filteredDepartments = departments.filter(
        (dept) => dept.trim() !== ""
      );
      const filteredDesignations = designations.filter(
        (desig) => desig.trim() !== ""
      );

      const updateDetailsBody = {
        project_id: projectId,
        project_details: {
          project_start_date: projectDetails.projectStartDate
            ? projectDetails.projectStartDate.format("YYYY-MM-DD")
            : null,
          client_spoc_name: projectDetails.spocName,
          study_period_begining_date: projectDetails.studyBeginDate
            ? projectDetails.studyBeginDate.format("YYYY-MM-DD")
            : null,
          study_period_end_date: projectDetails.studyEndDate
            ? projectDetails.studyEndDate.format("YYYY-MM-DD")
            : null,
          earliest_date_for_date_of_joining_relevent_for_the_study:
            projectDetails.earliestDateForDOJ
              ? projectDetails.earliestDateForDOJ.format("YYYY-MM-DD")
              : null,
          end_date_for_date_of_joining_relevent_for_the_study:
            projectDetails.endDateForDOJ
              ? projectDetails.endDateForDOJ.format("YYYY-MM-DD")
              : null,
          designations: filteredDesignations,
          departments: filteredDepartments,
        },
      };

      const updateDetailsResponse = await fetch(
        `${BASE_URL}/project/update_project_details`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateDetailsBody),
        }
      );

      const data = await updateDetailsResponse.json();
      // setInputId(data.latest_input);
      console.log(data.latest_input);

      if (updateDetailsResponse.ok) {
        messageApi.success("Project updated successfully!");
        const projectStatus = data.project_status;
        console.log("Project Status:", projectStatus);

        if (projectStatus == "started") {
          navigate(`/architect/${clientID}/${roleId}/${projectId}/kpi`, {
            state: { clientName },
          });
        } else if (projectStatus == "kpi_initialised") {
          navigate(`/architect/${clientID}/${roleId}/${projectId}/kpi`, {
            state: { clientName },
          });
        } else if (projectStatus == "input_sheet_generated") {
          navigate(`/architect/${projectId}/threshold`, {
            state: { inputID: data.latest_input, clientID, roleId, dataUploaded },
          });
        }
      } else {
        const errorData = await updateDetailsResponse.json();
        messageApi.error(
          errorData.detail || "Failed to update project details"
        );
      }
    } catch (error) {
      console.error("Error updating project:", error);
      messageApi.error("An error occurred while updating the project");
    }
  };

  const handleAddDepartment = () => {
    setDepartments([...departments, ""]);
    setErrors((prev) => ({ ...prev, departments: "" }));
  };

  const handleAddDesignation = () => {
    setDesignations([...designations, ""]);
    setErrors((prev) => ({ ...prev, designations: "" }));
  };

  const handleDeleteDepartment = (index) => {
    if (departments.length === 1) {
      setDepartments([""]);
      return;
    }
    setDepartments(departments.filter((_, itemIndex) => itemIndex !== index));
    setErrors((prev) => ({ ...prev, departments: "" }));
  };

  const handleDeleteDesignation = (index) => {
    if (designations.length === 1) {
      setDesignations([""]);
      return;
    }
    setDesignations(designations.filter((_, itemIndex) => itemIndex !== index));
    setErrors((prev) => ({ ...prev, designations: "" }));
  };

  const sanitizeNameInput = (value) =>
    value.replace(/[^a-zA-Z0-9\s]/g, "");

  const updateDepartment = (index, value) => {
    const sanitizedValue = sanitizeNameInput(value);
    const newDepartments = [...departments];
    newDepartments[index] = sanitizedValue;
    setDepartments(newDepartments);
    const nonEmptyDepartments = newDepartments
      .map((dept) => dept.trim().toLowerCase())
      .filter(Boolean);
    if (new Set(nonEmptyDepartments).size !== nonEmptyDepartments.length) {
      setErrors((prev) => ({
        ...prev,
        departments: "Duplicate department values are not allowed",
      }));
      return;
    }

    if (
      sanitizedValue.trim() !== "" ||
      newDepartments.some((dept) => dept.trim() !== "")
    ) {
      setErrors((prev) => ({ ...prev, departments: "" }));
    }
  };

  const updateDesignation = (index, value) => {
    const sanitizedValue = sanitizeNameInput(value);
    const newDesignations = [...designations];
    newDesignations[index] = sanitizedValue;
    setDesignations(newDesignations);
    const nonEmptyDesignations = newDesignations
      .map((designation) => designation.trim().toLowerCase())
      .filter(Boolean);
    if (new Set(nonEmptyDesignations).size !== nonEmptyDesignations.length) {
      setErrors((prev) => ({
        ...prev,
        designations: "Duplicate designation values are not allowed",
      }));
      return;
    }

    if (
      sanitizedValue.trim() !== "" ||
      newDesignations.some((desig) => desig.trim() !== "")
    ) {
      setErrors((prev) => ({ ...prev, designations: "" }));
    }
  };

  const handleProjectDetailChange = (field, value) => {
    setProjectDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({ ...prev, [field]: "" }));

    if (field === "studyBeginDate" || field === "studyEndDate") {
      setErrors((prev) => ({ ...prev, dateComparison: "" }));
    }
  };

  const ErrorMessage = ({ message }) =>
    message ? (
      <div
        className="error-message"
        style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
      >
        {message}
      </div>
    ) : null;

  if (isLoading) {
    // return <div className="loading">Loading project data...</div>;
    <Loading />;
  }

  const handleback = () => {
    navigate(`/architect/${roleId}/projects`);
  };

  const triggerCopyProjectModal = () => {
    // setCurrentProject(project);
    setShowCopyProjectModal(true);
  };

  const cancelCopyProject = () => {
    setShowCopyProjectModal(false);
  };

  const confirmCopyProject = () => {
    console.log("Copying project with details:", {
      projectName,
      clientName,
      departments,
      designations,
      projectDetails,
    });
    navigate(`/architect/${clientID}/${roleId}/new-project`, {
      state: {
        projectName,
        clientName,
        departments,
        designations,
        projectDetailsSend,
      },
    });
  };

  return (
    <>
      {contextHolder}
      <div className="editProject-sidebar"></div>
      {isLoading ? (
        <Loading />
      ) : (
        <div className="editproject-container">
          {/* Component 1: Project Info */}
          <div className="editproject-section">
            <div className="edit_header-buttons">
              <button className="editproject-header" onClick={handleback}>
                <span className="editproject-back-arrow">
                  <ChevronLeft />
                </span>
                <span className="editproject-title">
                  Project Initialization
                </span>
              </button>
              <button
                className="copydetails-button"
                onClick={triggerCopyProjectModal}
              >
                {/* <span className="editproject-plus-icon">+</span> */}
                Copy Project Details
              </button>
            </div>

            <div className="editproject-form-row">
              <div className="editproject-form-group">
                <label className="editproject-label">
                  Client's Name<span className="editproject-required">*</span>
                </label>
                <input
                  type="text"
                  className="editproject-input"
                  placeholder="Enter Client Name"
                  value={clientName}
                  onChange={(e) => {
                    setClientName(e.target.value);
                    if (errors.clientName) validateClientName();
                  }}
                  onBlur={validateClientName}
                  disabled
                />
                <ErrorMessage message={errors.clientName} />
              </div>
              <div className="editproject-form-group">
                <label className="editproject-label">
                  Project's Name<span className="editproject-required">*</span>
                </label>
                <input
                  type="text"
                  className="editproject-input"
                  placeholder="Enter Project Name"
                  value={projectName}
                  onChange={(e) => {
                    setProjectName(e.target.value);
                    if (errors.projectName) validateProjectName();
                  }}
                  onBlur={validateProjectName}
                />
                <ErrorMessage message={errors.projectName} />
              </div>
            </div>
          </div>

          {/* Component 2: Project Details (with all fields) */}
          <div className="editproject-section">
            <div className="editproject-form-grid">
              <div className="editproject-form-group">
                <label className="editproject-label">
                  Project SPOC Name
                  <span className="editproject-required">*</span>
                </label>
                <input
                  type="text"
                  className="editproject-input"
                  placeholder="Enter SPOC Name"
                  value={projectDetails.spocName}
                  onChange={(e) =>
                    handleProjectDetailChange("spocName", e.target.value)
                  }
                />
                <ErrorMessage message={errors.spocName} />
              </div>
              <div className="editproject-form-group">
                <label className="editproject-label">
                  Project Start Date
                  <span className="editproject-required">*</span>
                </label>
                <DatePicker
                  placeholder="Select Date"
                  className="editproject-datepicker"
                  value={projectDetails.projectStartDate}
                  onChange={(date) =>
                    handleProjectDetailChange("projectStartDate", date)
                  }
                  format="DD-MM-YYYY"
                  minDate={datePickerMinDate}
                  maxDate={datePickerMaxDate}
                  disabledDate={disabledDateOutsideRange}
                />
                <ErrorMessage message={errors.projectStartDate} />
              </div>
              <div className="editproject-form-group">
                <label className="editproject-label">
                  Study Period Beginning Date
                  <span className="editproject-required">*</span>
                </label>
                <DatePicker
                  placeholder="Select Date"
                  className="editproject-datepicker"
                  value={projectDetails.studyBeginDate}
                  onChange={(date) =>
                    handleProjectDetailChange("studyBeginDate", date)
                  }
                  format="DD-MM-YYYY"
                  minDate={datePickerMinDate}
                  maxDate={datePickerMaxDate}
                  disabledDate={disabledDateOutsideRange}
                />
                <ErrorMessage message={errors.studyBeginDate} />
              </div>
              <div className="editproject-form-group">
                <label className="editproject-label">
                  Study Period End Date
                  <span className="editproject-required">*</span>
                </label>
                <DatePicker
                  placeholder="Select Date"
                  className="editproject-datepicker"
                  value={projectDetails.studyEndDate}
                  onChange={(date) =>
                    handleProjectDetailChange("studyEndDate", date)
                  }
                  format="DD-MM-YYYY"
                  minDate={datePickerMinDate}
                  maxDate={datePickerMaxDate}
                  disabledDate={disabledDateOutsideRange}
                />
                <ErrorMessage message={errors.studyEndDate} />
                <ErrorMessage message={errors.dateComparison} />
              </div>

              <div className="editproject-form-group">
                <label className="editproject-label">
                  Earliest Date for DOJ Relevant for Study
                  <span className="editproject-required">*</span>
                </label>
                <DatePicker
                  className="editproject-datepicker"
                  value={projectDetails.earliestDateForDOJ}
                  onChange={(date) =>
                    handleProjectDetailChange("earliestDateForDOJ", date)
                  }
                  // disabled={true}
                  format="DD-MM-YYYY"
                  minDate={datePickerMinDate}
                  maxDate={datePickerMaxDate}
                  disabledDate={disabledDateOutsideRange}
                />
                {/* <small className="helper-text" style={{ fontSize: '11px', color: '#666' }}>
                                Auto-calculated based on study period
                            </small> */}
              </div>

              <div className="editproject-form-group">
                <label className="editproject-label">
                  End Date for DOJ Relevant for Study
                  <span className="editproject-required">*</span>
                </label>
                <DatePicker
                  className="editproject-datepicker"
                  value={projectDetails.endDateForDOJ}
                  onChange={(date) =>
                    handleProjectDetailChange("endDateForDOJ", date)
                  }
                  // disabled={true}
                  format="DD-MM-YYYY"
                  minDate={datePickerMinDate}
                  maxDate={datePickerMaxDate}
                  disabledDate={disabledDateOutsideRange}
                />
                {/* <small className="helper-text" style={{ fontSize: '11px', color: '#666' }}>
                                Same as study period end date
                            </small> */}
              </div>
            </div>
          </div>

          {/* Component 3: Department Covered in Study */}
          <div className="editproject-section">
            <div className="editproject-header-row">
              <h3 className="editproject-section-title">
                Department Covered in Study
                <span className="editproject-required">*</span>
              </h3>
              <button
                className="editproject-add-btn"
                onClick={handleAddDepartment}
              >
                {/* <span className="editproject-plus-icon">+</span> */}
                Add New Dept.
              </button>
            </div>

            <div className="editproject-department-container">
              {departments.map((dept, index) => (
                <div
                  className="editproject-department-item"
                  key={`dept-${index}`}
                >
                  <input
                    type="text"
                    className="editproject-department-input"
                    placeholder="Enter Department Name"
                    value={dept}
                    onChange={(e) => updateDepartment(index, e.target.value)}
                  />
                  <button
                    type="button"
                    className="editproject-delete-btn"
                    onClick={() => handleDeleteDepartment(index)}
                    aria-label="Delete department"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <ErrorMessage message={errors.departments} />
          </div>

          {/* Component 4: Designation Covered in Study */}
          <div className="editproject-section">
            <div className="editproject-header-row">
              <h3 className="editproject-section-title">
                Designation Covered in Study
                <span className="editproject-required">*</span>
              </h3>
              <button
                className="editproject-add-btn"
                onClick={handleAddDesignation}
              >
                {/* <span className="editproject-plus-icon">+</span> */}
                Add New Designation
              </button>
            </div>

            <div className="editproject-designation-container">
              {designations.map((designation, index) => (
                <div
                  className="editproject-designation-item"
                  key={`desig-${index}`}
                >
                  <input
                    type="text"
                    className="editproject-designation-input"
                    placeholder="Enter Designation Name"
                    value={designation}
                    onChange={(e) => updateDesignation(index, e.target.value)}
                  />
                  <button
                    type="button"
                    className="editproject-delete-btn"
                    onClick={() => handleDeleteDesignation(index)}
                    aria-label="Delete designation"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <ErrorMessage message={errors.designations} />
          </div>

          {/* Next Button */}
          <div className="editproject-next-btn-container">
            <button className="editproject-next-btn" onClick={handleSubmit}>
              NEXT
            </button>
          </div>
        </div>
      )}

      {showCopyProjectModal && (
        <div className="confirm-copyproject-modal-overlay-unique">
          <div className="confirm-copyproject-modal-container-unique">
            <div className="confirm-copyproject-modal-content-unique">
              <h3>Are you sure you want to copy this project details?</h3>
              <div className="confirm-copyproject-modal-buttons-unique">
                <button
                  className="confirm-copyproject-modal-button-unique confirm-copyproject-cancel-unique"
                  onClick={cancelCopyProject}
                >
                  No
                </button>
                <button
                  className="confirm-copyproject-modal-button-unique confirm-copyproject-confirm-unique"
                  onClick={confirmCopyProject}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditProject;
